import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Badge, ButtonGroup, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaClipboardList, FaFileInvoiceDollar, FaCalendarAlt, FaSearch } from 'react-icons/fa';
import { getEstimates } from '../services/estimateService';
import { getBids } from '../services/bidService';
import './Dashboard.css';

const Dashboard = () => {
  const [estimates, setEstimates] = useState([]);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch only recent items for dashboard performance (limit at API level)
        const [estimatesData, bidsData] = await Promise.all([
          getEstimates(50),  // Limit to 50 most recent at database level
          getBids(50)
        ]);

        setEstimates(estimatesData);
        setBids(bidsData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const scheduledEstimates = estimates.filter(e => e.scheduled_date);
  const draftBids = bids.filter(b => b.status === 'draft');
  
  // Create recent actions by combining all activities
  const getRecentActions = () => {
    const actions = [];

    // Add estimates as actions
    estimates.forEach(estimate => {
      actions.push({
        id: `estimate-${estimate.id}`,
        type: 'estimate',
        title: `EST-${estimate.id}`,
        description: `Estimate created for ${estimate.customer_name}`,
        customerName: estimate.customer_name,
        status: estimate.status,
        date: estimate.created_at,
        link: '/estimates'
      });
    });

    // Add bids as actions
    bids.forEach(bid => {
      actions.push({
        id: `bid-${bid.id}`,
        type: 'bid',
        title: `BID-${bid.id}`,
        description: `Bid ${bid.status} for ${bid.customer_name}`,
        customerName: bid.customer_name,
        status: bid.status,
        date: bid.updated_at || bid.created_at,
        link: '/bids'
      });
    });

    // Sort by date (most recent first)
    return actions.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Filter actions based on search and filters
  const getFilteredActions = () => {
    let filtered = getRecentActions();

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(action =>
        action.title.toLowerCase().includes(search) ||
        action.description.toLowerCase().includes(search) ||
        action.customerName?.toLowerCase().includes(search) ||
        action.region?.toLowerCase().includes(search)
      );
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(action => action.type === typeFilter);
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(action => action.status === statusFilter);
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      filtered = filtered.filter(action => {
        const actionDate = new Date(action.date);

        switch (dateFilter) {
          case 'today':
            return actionDate >= today;
          case 'week': {
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return actionDate >= weekAgo;
          }
          case 'month': {
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return actionDate >= monthAgo;
          }
          default:
            return true;
        }
      });
    }

    // Return top 20 results
    return filtered.slice(0, 20);
  };

  // Get unique statuses for filter
  const getUniqueStatuses = () => {
    const statuses = new Set();
    getRecentActions().forEach(action => {
      if (action.status) statuses.add(action.status);
    });
    return Array.from(statuses).sort();
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Date Null : Not Scheduled';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Date Null : Not Scheduled';

    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
  };

  const getActionIcon = (type) => {
    switch (type) {
      case 'estimate':
        return <FaClipboardList className="action-icon estimates-color" />;
      case 'bid':
        return <FaFileInvoiceDollar className="action-icon bids-color" />;
      default:
        return <FaCalendarAlt className="action-icon" />;
    }
  };
  
  return (
    <div className="dashboard-container">
      <Row>
        <Col md={6} sm={6}>
          <Card className="dashboard-card">
            <Card.Body>
              <div className="card-icon-container estimates-icon">
                <FaClipboardList className="card-icon" />
              </div>
              <h3 className="metric-value">{loading ? '—' : scheduledEstimates.length}</h3>
              <p className="metric-label">Scheduled Estimates</p>
            </Card.Body>
            <Card.Footer>
              <Link to="/estimates">View All Estimates</Link>
            </Card.Footer>
          </Card>
        </Col>

        <Col md={6} sm={6}>
          <Card className="dashboard-card">
            <Card.Body>
              <div className="card-icon-container bids-icon">
                <FaFileInvoiceDollar className="card-icon" />
              </div>
              <h3 className="metric-value">{loading ? '—' : draftBids.length}</h3>
              <p className="metric-label">Draft Bids</p>
            </Card.Body>
            <Card.Footer>
              <Link to="/bids">View All Bids</Link>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
      
      <Row className="mt-4">
        <Col md={12}>
          <Card className="recent-actions-card">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <span>Recent Actions</span>
                <Badge bg="secondary" pill>{loading ? '...' : getFilteredActions().length}</Badge>
              </div>
            </Card.Header>
            <Card.Body>
              {/* Search Bar */}
              <div className="dashboard-search-container mb-3">
                <div className="search-input-wrapper">
                  <FaSearch className="search-icon" />
                  <Form.Control
                    type="text"
                    placeholder="Search by title, customer, or region..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="dashboard-search-input"
                  />
                  {searchTerm && (
                    <button
                      className="clear-search-btn"
                      onClick={() => setSearchTerm('')}
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {/* Filter Tags */}
              <div className="dashboard-filters mb-3">
                {/* Type Filter */}
                <div className="filter-group">
                  <label className="filter-label">Type:</label>
                  <ButtonGroup size="sm">
                    <Button
                      variant={typeFilter === 'all' ? 'primary' : 'outline-secondary'}
                      onClick={() => setTypeFilter('all')}
                    >
                      All
                    </Button>
                    <Button
                      variant={typeFilter === 'estimate' ? 'primary' : 'outline-secondary'}
                      onClick={() => setTypeFilter('estimate')}
                    >
                      Estimates
                    </Button>
                    <Button
                      variant={typeFilter === 'bid' ? 'primary' : 'outline-secondary'}
                      onClick={() => setTypeFilter('bid')}
                    >
                      Bids
                    </Button>
                  </ButtonGroup>
                </div>

                {/* Status Filter */}
                <div className="filter-group">
                  <label className="filter-label">Status:</label>
                  <ButtonGroup size="sm">
                    <Button
                      variant={statusFilter === 'all' ? 'primary' : 'outline-secondary'}
                      onClick={() => setStatusFilter('all')}
                    >
                      All
                    </Button>
                    {getUniqueStatuses().map(status => (
                      <Button
                        key={status}
                        variant={statusFilter === status ? 'primary' : 'outline-secondary'}
                        onClick={() => setStatusFilter(status)}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </Button>
                    ))}
                  </ButtonGroup>
                </div>

                {/* Date Filter */}
                <div className="filter-group">
                  <label className="filter-label">Period:</label>
                  <ButtonGroup size="sm">
                    <Button
                      variant={dateFilter === 'all' ? 'primary' : 'outline-secondary'}
                      onClick={() => setDateFilter('all')}
                    >
                      All Time
                    </Button>
                    <Button
                      variant={dateFilter === 'today' ? 'primary' : 'outline-secondary'}
                      onClick={() => setDateFilter('today')}
                    >
                      Today
                    </Button>
                    <Button
                      variant={dateFilter === 'week' ? 'primary' : 'outline-secondary'}
                      onClick={() => setDateFilter('week')}
                    >
                      This Week
                    </Button>
                    <Button
                      variant={dateFilter === 'month' ? 'primary' : 'outline-secondary'}
                      onClick={() => setDateFilter('month')}
                    >
                      This Month
                    </Button>
                  </ButtonGroup>
                </div>
              </div>

              {/* Results */}
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mt-2">Loading dashboard data...</p>
                </div>
              ) : getFilteredActions().length === 0 ? (
                <p className="text-muted text-center py-4">
                  {searchTerm || typeFilter !== 'all' || statusFilter !== 'all' || dateFilter !== 'all'
                    ? 'No actions match your search or filters.'
                    : 'No recent actions found.'}
                </p>
              ) : (
                <div className="recent-actions-list">
                  {getFilteredActions().map(action => (
                    <div className="recent-action-item" key={action.id}>
                      <div className="action-icon-wrapper">
                        {getActionIcon(action.type)}
                      </div>
                      <div className="action-content">
                        <div className="action-header">
                          <span className="action-title">{action.title}</span>
                          <div className="action-badges">
                            {action.region && (
                              <span className="region-label">{action.region}</span>
                            )}
                            <span className={`status-badge status-${action.status}`}>
                              {action.status ? action.status.charAt(0).toUpperCase() + action.status.slice(1) : 'No Status'}
                            </span>
                          </div>
                        </div>
                        <div className="action-details">
                          <span className="action-description">{action.description}</span>
                          <span className="action-date">{formatDate(action.date)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
